import { cn } from "@/lib/utils";

// ─── Single Sparkle SVG icon ────────────────────────────────────────────────
interface SparkleIconProps {
  size?: number;
  className?: string;
  style?: React.CSSProperties;
}

export function SparkleIcon({ size = 24, className, style }: SparkleIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={cn("text-yellow-400", className)}
      style={{ filter: "drop-shadow(0 0 4px #fbbf24)", ...style }}
    >
      <path d="M12 0 L13.8 10.2 L24 12 L13.8 13.8 L12 24 L10.2 13.8 L0 12 L10.2 10.2 Z" />
    </svg>
  );
}

// Alias: several call sites import the base icon as `Sparkle`.
export const Sparkle = SparkleIcon;

// ─── Inline sparkle (sits next to text) ────────────────────────────────────
interface InlineSparkleProps {
  size?: number;
  className?: string;
  style?: React.CSSProperties;
}

export function InlineSparkle({ size = 20, className, style }: InlineSparkleProps) {
  return (
    <SparkleIcon
      size={size}
      className={cn("inline-block align-middle animate-pulse", className)}
      style={style}
    />
  );
}

// ─── Ping-animated sparkle (notification dot style) ────────────────────────
interface PingSparkleProps {
  size?: number;
  className?: string;
}

export function PingSparkle({ size = 18, className }: PingSparkleProps) {
  return (
    <span className={cn("relative inline-flex", className)}>
      <SparkleIcon size={size} className="animate-ping absolute text-yellow-400 opacity-75" />
      <SparkleIcon size={size} className="relative text-yellow-400" />
    </span>
  );
}

// ─── Radial burst (8 sparkles explode outward, one-shot) ───────────────────
interface SparkleBurstProps {
  count?: number;
  className?: string;
  onDone?: () => void;
}

const BURST_POSITIONS = [
  { top: "0%", left: "50%", delay: "0ms" },
  { top: "15%", left: "85%", delay: "60ms" },
  { top: "50%", left: "100%", delay: "120ms" },
  { top: "85%", left: "85%", delay: "180ms" },
  { top: "100%", left: "50%", delay: "240ms" },
  { top: "85%", left: "15%", delay: "300ms" },
  { top: "50%", left: "0%", delay: "360ms" },
  { top: "15%", left: "15%", delay: "420ms" },
];

export function SparkleBurst({ count = 8, className }: SparkleBurstProps) {
  const positions = BURST_POSITIONS.slice(0, count);
  return (
    <span
      className={cn("pointer-events-none absolute inset-0 scale-150", className)}
      aria-hidden="true"
    >
      {positions.map((pos, i) => (
        <SparkleIcon
          key={i}
          size={22}
          className="absolute text-yellow-400 animate-ping"
          style={{
            top: pos.top,
            left: pos.left,
            transform: "translate(-50%, -50%)",
            animationDelay: pos.delay,
            animationDuration: "700ms",
            animationIterationCount: "1",
            animationFillMode: "forwards",
          } as React.CSSProperties}
        />
      ))}
    </span>
  );
}

// ─── Full-screen overlay burst (onboarding success) ────────────────────────
interface FullScreenSparkleProps {
  durationMs?: number;
  onDone?: () => void;
}

const FS_POSITIONS = [
  { top: "10%", left: "15%" }, { top: "20%", left: "80%" },
  { top: "35%", left: "5%" }, { top: "35%", left: "95%" },
  { top: "50%", left: "30%" }, { top: "50%", left: "70%" },
  { top: "65%", left: "10%" }, { top: "65%", left: "90%" },
  { top: "80%", left: "25%" }, { top: "80%", left: "75%" },
  { top: "90%", left: "50%" }, { top: "5%", left: "50%" },
];

export function FullScreenSparkle({ durationMs = 1500, onDone }: FullScreenSparkleProps) {
  return (
    <div
      className="fixed inset-0 z-50 pointer-events-none overflow-hidden"
      aria-hidden="true"
      style={{ animation: `fadeOut ${durationMs}ms ease-out forwards` }}
      onAnimationEnd={onDone}
    >
      {FS_POSITIONS.map((pos, i) => (
        <SparkleIcon
          key={i}
          size={i % 3 === 0 ? 36 : i % 3 === 1 ? 28 : 20}
          className="absolute text-yellow-400 animate-bounce"
          style={{
            top: pos.top,
            left: pos.left,
            animationDelay: `${i * 80}ms`,
            animationDuration: "400ms",
          } as React.CSSProperties}
        />
      ))}
      <style>{`@keyframes fadeOut { 0%{opacity:1} 70%{opacity:1} 100%{opacity:0} }`}</style>
    </div>
  );
}

// ─── Orbiting sparkle (slow rotation around a point) ───────────────────────
interface OrbitSparkleProps {
  size?: number;
  className?: string;
}

export function OrbitSparkle({ size = 16, className }: OrbitSparkleProps) {
  return (
    <span
      className={cn("absolute -top-1 -right-1 pointer-events-none", className)}
      style={{ animation: "orbit 3s linear infinite" }}
      aria-hidden="true"
    >
      <SparkleIcon size={size} className="text-yellow-400" />
      <style>{`@keyframes orbit { 0%{transform:rotate(0deg) translateX(12px) rotate(0deg)} 100%{transform:rotate(360deg) translateX(12px) rotate(-360deg)} }`}</style>
    </span>
  );
          }
